import i18n from "@/i18n";
import type { PracticePack, PracticeScenario } from "@/types";

/** i18n sub-keys mirror the backend scenario slugs (e.g. "break_ice"). */
function scenarioKey(scenario: PracticeScenario): string | null {
  return scenario.slug ? scenario.slug : null;
}

/**
 * i18next answers with the KEY itself when it has no entry for one.
 *
 * Which is fine for a value nobody reads and wrong for a card title: an
 * unlocalized scenario would put `practice.scenarios.new_one.title` on screen.
 * The server sends usable English for every scenario, so that is the answer
 * whenever the catalogue has none. `practice-scenario-copy.test.ts` stops a
 * seeded slug reaching that state in the first place; this is the net under it,
 * for a scenario seeded by an API that is ahead of this build.
 */
function translatedOr(key: string, fallback: string): string {
  const value = i18n.t(key);
  return value && value !== key ? value : fallback;
}

export function practiceScenarioCopy(scenario: PracticeScenario): {
  title: string;
  description: string;
} {
  const key = scenarioKey(scenario);
  // No slug means a scenario the user wrote. Their words, never translated.
  if (!key) {
    return { title: scenario.title, description: scenario.description };
  }
  return {
    title: translatedOr(`practice.scenarios.${key}.title`, scenario.title),
    description: translatedOr(
      `practice.scenarios.${key}.description`,
      scenario.description,
    ),
  };
}

/** Localized guided opening (scene, goal, and 2–3 starter options). */
export type GuidedOpening = {
  scene: string;
  goal: string;
  starters: string[];
};

export function guidedOpening(scenario: PracticeScenario): GuidedOpening | null {
  const key = scenarioKey(scenario);
  if (!key) return null;
  const starters = ["1", "2", "3"]
    .map((n) => i18n.t(`practice.scenarios.${key}.starters.${n}`))
    .filter((s) => s && !s.startsWith("practice.")); // drop any missing key
  const scene = i18n.t(`practice.scenarios.${key}.scene`);
  const goal = i18n.t(`practice.scenarios.${key}.goal`);
  // The starters were already guarded; the scene and goal were not, so an
  // unlocalized scenario put two raw key paths at the top of the panel. There
  // is no English to fall back on here — the server's `openingPrompt` is a
  // provider hint, not copy — so the honest answer is no guided opening at
  // all, which the caller already handles.
  if (scene.startsWith("practice.") || goal.startsWith("practice.")) return null;
  return { scene, goal, starters };
}

export function practicePackLabel(pack: PracticePack): string {
  return i18n.t("practice.packReplies", { count: pack.replies });
}
